import { NgZone } from '@angular/core';

import { WatchProgress } from './embed-video.model';

/** Share of the timeline that must have been actually walked for a gated video to complete. */
export const COMPLETION_COVERAGE = 0.95;

/**
 * Switch for the real-coverage requirement (decision 40 of the learning path's CLAUDE.md, revised
 * by decision 68).
 *
 * `false` (today, the owner's call — she does not want it strict): a provider's `ended` is enough to
 * give the content as watched, even if the learner dragged the scrubber. Reaching the last
 * `NEAR_END_SECONDS` is enough too, which is what unblocks the environments that never deliver
 * `ended` at all (youtube on iOS, vimeo in the hybrid app).
 *
 * `true`: goes back to requiring `COMPLETION_COVERAGE` of timeline actually walked before completing
 * a content whose block carries `review_all_duration_media`.
 *
 * **Nothing that measures it is switched off by this.** `covered`, `coverage()` and the `coverage`
 * that travels in every `watchProgress` keep working exactly the same. The only thing this switch
 * changes is whether that number **decides** or merely **reports** — turning it back on is editing
 * this one line and nothing else.
 *
 * Two things to fix *before* turning it on, both written up in CLAUDE.md (decision 68): a
 * background tab's throttled `setInterval` destroys coverage for someone who did watch the whole
 * video, and a drag + pause + reload seeds coverage that was never earned.
 *
 * Typed `: boolean` on purpose — without it TypeScript narrows it to the literal `false` and the two
 * branches that read it below are flagged as unreachable in the editor.
 */
export const ENFORCE_WATCH_COVERAGE: boolean = false;

/** Seconds from the end that count as "about to finish" — same window `videoWWillEnd` already uses. */
const NEAR_END_SECONDS = 3;
const SAMPLE_INTERVAL_MS = 400;
/** A gap larger than this between two samples is a jump (scrub/seek), not normal playback. */
const CONTIGUOUS_GAP_SECONDS = 1.5;

export interface WatchCompletionCallbacks {
  onProgress: (progress: WatchProgress) => void;
  onNearEnd: () => void;
  onCompleted: () => void;
}

/**
 * Measures real timeline coverage and owns the completion decision for an embedded video.
 * Agnostic of the source (only needs `currentTime`/`duration`), so the day a classic `<video>` /
 * `<audio>` adopts it, it works unchanged.
 *
 * Completes exactly once. **With `ENFORCE_WATCH_COVERAGE` off (today)** either path does it:
 * - the provider's own `ended` arrived — dragging the scrubber to the end counts as watched;
 * - no `ended` ever arrived (today: iOS youtube, hybrid vimeo) but the last `NEAR_END_SECONDS` were
 *   reached anyway — the safety net production does not have.
 *
 * **With the switch on**, both paths additionally require `COMPLETION_COVERAGE` of the timeline to
 * have been genuinely walked whenever the block carries `review_all_duration_media`. Coverage only
 * credits **contiguous** playback: a forward seek credits the single second it landed on, never the
 * seconds it jumped over, so a gated video could not be faked complete by dragging. That is the
 * behaviour the switch brings back; the measurement itself never stopped running.
 */
export class WatchCompletionTracker {
  private readonly covered = new Set<number>();
  private lastSampleTime: number | null = null;
  private duration = 0;
  private nearEndReached = false;
  private endedReceived = false;
  private completed = false;
  private tickHandle: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly zone: NgZone,
    private readonly reviewAllRequired: boolean,
    private readonly callbacks: WatchCompletionCallbacks,
    resumeFrom = 0
  ) {
    // Resuming is not skipping: whatever the backend already has as `ultima_posicion` was genuinely
    // watched in an earlier session, so it is seeded as already-covered ground.
    for (let second = 0; second <= Math.floor(resumeFrom); second++) this.covered.add(second);
    if (resumeFrom > 0) this.lastSampleTime = resumeFrom;
  }

  /** Starts sampling outside Angular — no change detection runs on every tick (same as `draggable-sheet`). */
  start(getCurrentTime: () => number, getDuration: () => number): void {
    this.zone.runOutsideAngular(() => {
      this.tickHandle = setInterval(() => this.sample(getCurrentTime(), getDuration()), SAMPLE_INTERVAL_MS);
    });
  }

  /** A provider's own `ended` event — one of the two paths to completion (see the class doc). */
  notifyEnded(): void {
    this.endedReceived = true;
    if (!ENFORCE_WATCH_COVERAGE || !this.reviewAllRequired || this.coverage() >= COMPLETION_COVERAGE) {
      this.markCompleted();
    }
  }

  stop(): void {
    if (this.tickHandle !== null) {
      clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
  }

  private sample(currentTime: number, duration: number): void {
    if (!isFinite(currentTime) || !isFinite(duration) || duration <= 0) return;

    const previous = this.lastSampleTime;
    if (previous === null || currentTime < previous || currentTime - previous > CONTIGUOUS_GAP_SECONDS) {
      // First sample, a rewind, or a jump: credit only the single second landed on — a forward seek
      // never credits the seconds it skipped over.
      this.covered.add(Math.floor(currentTime));
    } else {
      for (let second = Math.floor(previous); second <= Math.floor(currentTime); second++) {
        this.covered.add(second);
      }
    }
    this.lastSampleTime = currentTime;
    this.duration = duration;

    const nearEnd = duration - currentTime <= NEAR_END_SECONDS;
    if (nearEnd && !this.nearEndReached) {
      this.nearEndReached = true;
      this.zone.run(() => this.callbacks.onNearEnd());
    }

    this.zone.run(() =>
      this.callbacks.onProgress({ position: currentTime, duration, coverage: this.coverage(), nearEnd })
    );

    // The safety net: an environment that never delivers `ended` still completes, instead of
    // silently never reviewing the content (today's production behaviour). With the switch off,
    // reaching the end is the whole condition; with it on, the timeline must also have been covered.
    if (
      !this.endedReceived &&
      nearEnd &&
      (!ENFORCE_WATCH_COVERAGE || this.coverage() >= COMPLETION_COVERAGE)
    ) {
      this.markCompleted();
    }
  }

  private coverage(): number {
    if (this.duration <= 0) return 0;
    return Math.min(1, this.covered.size / Math.ceil(this.duration));
  }

  private markCompleted(): void {
    if (this.completed) return;
    this.completed = true;
    this.stop();
    this.zone.run(() => this.callbacks.onCompleted());
  }
}
